"use client";

import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import get from "lodash/get";
import { useRouter } from "next/navigation";
import { useCallback } from "react";
import { peopleRepositoryV2 } from "@/repositories/people/people-v2";
import type { Person } from "@/repositories/people/types";
import usePersistedBoundStore from "@/store/persisted-bound-store";

const THIRTY_MINUTES_MS = 1_800_000;
const ONE_HOUR_MS = 3_600_000;

interface PersonGetMeResponse {
  data: Partial<Person>;
}

type PersonGetMeQueryKey = ["getMe"];
type PersonGetMeOptions = Partial<
  UseQueryOptions<PersonGetMeResponse, Error, PersonGetMeResponse, PersonGetMeQueryKey>
>;

export function usePersonGetMe(
  options?: PersonGetMeOptions
): UseQueryResult<PersonGetMeResponse, Error> {
  const realm = usePersistedBoundStore.use.realm();
  const router = useRouter();

  const queryFn = useCallback(async (): Promise<PersonGetMeResponse> => {
    try {
      const response = await peopleRepositoryV2.getMe({ Realm: realm || "evcore" });
      return (await response.json()) as PersonGetMeResponse;
    } catch (error: unknown) {
      const errorResponse = error as AxiosError;
      const message = get(errorResponse, "message", "");
      if (
        typeof message === "string" &&
        message.includes("Usuario en auth no tienen atributos correctamente definidos")
      ) {
        router.push("/auth/register/normal");
      }
      throw error;
    }
  }, [realm, router]);

  return useQuery({
    queryKey: ["getMe"],
    queryFn,
    staleTime: THIRTY_MINUTES_MS,
    gcTime: ONE_HOUR_MS,
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    enabled: Boolean(
      typeof window !== "undefined" && localStorage.getItem("persisted-bound-store")
    ),
    ...options,
  });
}
